import { useCallback } from 'react';
import { Trans } from '@lingui/macro';
import {
    DETAIL_NO_NEED_ALERT,
    HYPERVISOR_FILTER_ID,
    NAME_FIELD,
    NEW_VCENTER_BREAD_CRUMB_TEXT,
    POLLING_STATUS_DISABLED_VALUE,
    POLLING_STATUS_DOWN_VALUE,
    SUBTYPE_FIELD,
    VIRTUAL_DETAIL_ENABLE_DISABLE_URLS,
    VIRTUAL_INFRASTRUCTURE_DETAIL_FUNCTION_NAME,
    VIRTUAL_INFRASTRUCTURE_TITLE,
    VIRTUAL_MACHINE_FILTER_ID,
    VMWARE_HOST_DETAIL,
    VMWARE_VM_DETAIL,
} from '../infrastructure/InfrastructureConstants';
import {
    HYPERVISOR_TYPE,
    VCENTER_TYPE,
    VIRTUALMACHINE_TYPE,
} from '../../filters/virtual-infrastructure-type-filter/SubTypeHelper';
import ConfirmDataTableActionButton from '../../form/ConfirmDataTableActionButton';
import { DELETE, DISABLE, ENABLE } from '../../../services/FormDataTableActionService';
import VirtualDetailSection from '../infrastructure/virtual/VirtualDetailSection';
import VirtualPropertiesSection from '../infrastructure/virtual/VirtualPropertiesSection';
import VirtualTablesSection from '../infrastructure/virtual/VirtualTablesSection';
import CollectorListSelector from '../infrastructure/virtual/CollectorListSelector';
import VcenterVerify from '../infrastructure/virtual/VcenterVerify';
import InfrastructureDetailBase from '../infrastructure/InfrastructureDetailBase';
import { buildPageId } from '../AdminLinkUtils';
import { createSimpleDrillDownBuilder } from '../infrastructure/utils/DrillDownUtils';
import { useVirtualInfrastructureDetailInit } from '../infrastructure/hooks/UseVirtualInfrastructureDetailInit';

const DRILL_DOWN_PAGES = {
    [HYPERVISOR_TYPE]: VMWARE_HOST_DETAIL,
    [VIRTUALMACHINE_TYPE]: VMWARE_VM_DETAIL,
};

const DRILL_DOWN_FILTERS = {
    [HYPERVISOR_TYPE]: HYPERVISOR_FILTER_ID,
    [VIRTUALMACHINE_TYPE]: VIRTUAL_MACHINE_FILTER_ID,
};

const getTargetConfig = createSimpleDrillDownBuilder(DRILL_DOWN_PAGES, DRILL_DOWN_FILTERS);

const GO_TO_URL = '../';
const BASE_URL = '/ipm/v1/admin/virtualInfrastructureConfig/';
const CREATE_VCENTER_URL = `${BASE_URL}/createVCenter`;

const FUNCTION_BREADCRUMBS = [{ link: GO_TO_URL, text: VIRTUAL_INFRASTRUCTURE_TITLE }];

export default function VirtualInfrastructureDetail() {
    const {
        recordId,
        isVcenterCreateAction,
        dataTableActionErrorHandler,
        selectionCollectorRef,
        setSaveButtonDisabled,
        getConfirmMsg,
        createSubmitTransform,
        isSaveDisabled,
        defaultValues,
    } = useVirtualInfrastructureDetailInit();

    const renderActionButtons = useCallback(
        ({ showDataTableActionButton, isActioning, recordType, formData, handleConfirmedDataTableAction }) => {
            if (!showDataTableActionButton) {
                return null;
            }

            const uiState = formData?.uiState;
            const isVCenter = recordType === VCENTER_TYPE;
            const isVirtualMachine = recordType === VIRTUALMACHINE_TYPE;
            const isDisabledOrDown = uiState === POLLING_STATUS_DISABLED_VALUE || uiState === POLLING_STATUS_DOWN_VALUE;

            return (
                <>
                    {isVCenter && (
                        <ConfirmDataTableActionButton
                            key={DELETE}
                            label={<Trans>DELETE vCenter</Trans>}
                            disabled={isActioning}
                            handleConfirmedDataTableAction={(event) =>
                                handleConfirmedDataTableAction(event, DELETE, true)
                            }
                            confirmMsg={() => getConfirmMsg(DELETE, recordType)}
                            plural={false}
                            needConfirm
                        />
                    )}
                    <ConfirmDataTableActionButton
                        key={DISABLE}
                        label={<Trans>DISABLE</Trans>}
                        disabled={isActioning || isDisabledOrDown}
                        handleConfirmedDataTableAction={(event) =>
                            handleConfirmedDataTableAction(event, DISABLE, DETAIL_NO_NEED_ALERT)
                        }
                        confirmMsg={() => getConfirmMsg(DISABLE, recordType)}
                        plural={false}
                        needConfirm={!isVirtualMachine}
                    />
                    <ConfirmDataTableActionButton
                        key={ENABLE}
                        label={<Trans>ENABLE</Trans>}
                        disabled={isActioning || !isDisabledOrDown}
                        handleConfirmedDataTableAction={(event) =>
                            handleConfirmedDataTableAction(event, ENABLE, DETAIL_NO_NEED_ALERT)
                        }
                        confirmMsg={() => getConfirmMsg(ENABLE, recordType)}
                        plural={false}
                        needConfirm
                    />
                </>
            );
        },
        [getConfirmMsg]
    );

    const renderFormContent = useCallback(
        ({ isCreateAction }) => (
            <>
                <VirtualDetailSection />
                <VirtualPropertiesSection />
                <VirtualTablesSection />
                {isCreateAction && (
                    <>
                        <CollectorListSelector
                            selectionCollectorRef={selectionCollectorRef}
                            setSaveButtonDisabled={setSaveButtonDisabled}
                        />
                        <VcenterVerify selectionCollectorRef={selectionCollectorRef} />
                    </>
                )}
            </>
        ),
        [selectionCollectorRef, setSaveButtonDisabled]
    );

    return (
        <InfrastructureDetailBase
            recordId={recordId}
            showSaveButton
            tableSelectionAction={false}
            baseUrl={isVcenterCreateAction ? CREATE_VCENTER_URL : BASE_URL}
            goToUrl={GO_TO_URL}
            defaultValues={defaultValues}
            pageId={buildPageId(VIRTUAL_INFRASTRUCTURE_DETAIL_FUNCTION_NAME)}
            nameField={NAME_FIELD}
            typeField={SUBTYPE_FIELD}
            newRecordBreadcrumbText={NEW_VCENTER_BREAD_CRUMB_TEXT}
            functionBreadcrumbs={FUNCTION_BREADCRUMBS}
            actionUrls={VIRTUAL_DETAIL_ENABLE_DISABLE_URLS}
            errorHandler={dataTableActionErrorHandler}
            createSubmitTransform={createSubmitTransform}
            getTargetConfig={getTargetConfig}
            isSaveDisabled={isSaveDisabled}
            actionButtonsFn={renderActionButtons}
        >
            {renderFormContent}
        </InfrastructureDetailBase>
    );
}
